import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { td } from '@/lib/i18n';
import { Activity, AlarmClock, Banknote, Building2, Camera, CarFront, CheckCircle2, Crown, Link2, Send, TrendingUp, UserCog, Webhook, XCircle, } from 'lucide-react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fetchDriverPayouts } from '@/services/driver-payout';
import { fetchDrivers } from '@/services/driver';
import { fetchCampaignAssignments } from '@/services/campaign-assignment';
import { fetchActivity, fetchLeaderboards, fetchTrends, fetchWorkspaceOverview } from '@/services/internal-dashboard';
import { fetchTelegramWebhookInfo } from '@/services/internal-setting';
import { isInternalAdmin } from '@/lib/roles';
import { useAuthenticationState } from '@/hooks/use-authentication';
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/')({
    component: InternalDashboard,
});
const TILES = [
    {
        to: '/console/internal/payouts',
        labelKey: 'dashboard.tile.payout_queue',
        descriptionKey: 'dashboard.tile.payout_queue_desc',
        Icon: Banknote,
    },
    {
        to: '/console/internal/photos/new',
        labelKey: 'dashboard.tile.log_photo',
        descriptionKey: 'dashboard.tile.log_photo_desc',
        Icon: Camera,
    },
    {
        to: '/console/internal/drivers',
        labelKey: 'dashboard.tile.drivers',
        descriptionKey: 'dashboard.tile.drivers_desc',
        Icon: CarFront,
    },
    {
        to: '/console/internal/assignments',
        labelKey: 'dashboard.tile.assignments',
        descriptionKey: 'dashboard.tile.assignments_desc',
        Icon: Link2,
    },
    {
        to: '/console/internal/team',
        labelKey: 'dashboard.tile.team',
        descriptionKey: 'dashboard.tile.team_desc',
        Icon: UserCog,
        adminOnly: true,
    },
    {
        to: '/console/internal/cron',
        labelKey: 'dashboard.tile.cron',
        descriptionKey: 'dashboard.tile.cron_desc',
        Icon: AlarmClock,
        adminOnly: true,
    },
];
function InternalDashboard() {
    const { t } = useTranslation('internal');
    const { user } = useAuthenticationState();
    const isAdmin = isInternalAdmin(user);
    const { data: queue = [] } = useQuery({
        queryKey: ['driver-payouts', 'due-overdue'],
        queryFn: () => fetchDriverPayouts(['due', 'overdue']),
    });
    const { data: drivers = [] } = useQuery({ queryKey: ['drivers'], queryFn: fetchDrivers });
    const { data: assignments = [] } = useQuery({
        queryKey: ['campaign-assignments'],
        queryFn: fetchCampaignAssignments,
    });
    const overdue = queue.filter((p) => p.state === 'overdue').length;
    const due = queue.filter((p) => p.state === 'due').length;
    const missingPhotos = queue.filter((p) => !p.hasPhoto).length;
    const visibleTiles = TILES.filter((tile) => !tile.adminOnly || isAdmin);
    return (<div className="flex flex-col gap-6 p-6">
            <div className="space-y-1">
                <h1 className="text-3xl font-semibold">{t('dashboard.title')}</h1>
                <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
            </div>

            <div className="grid gap-4 md:grid-cols-4">
                <Kpi label={t('dashboard.kpi.overdue_payouts')} value={overdue} accent={overdue > 0 ? 'danger' : 'default'}/>
                <Kpi label={t('dashboard.kpi.due_payouts')} value={due} accent={due > 0 ? 'warning' : 'default'}/>
                <Kpi label={t('dashboard.kpi.missing_photos')} value={missingPhotos}/>
                <Kpi label={t('dashboard.kpi.active_drivers')} value={drivers.length}/>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    <TrendsCard />
                </div>
                <TelegramHealthCard />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <ActivityCard />
                <LeaderboardsCard />
            </div>

            {isAdmin && <WorkspaceOverviewCard />}

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {visibleTiles.map(({ to, labelKey, descriptionKey, Icon }) => (<Link key={to} to={to} className="block">
                        <Card className="h-full hover:border-primary transition-colors">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Icon className="h-5 w-5"/>
                                    {td(t, labelKey)}
                                </CardTitle>
                                <CardDescription>{td(t, descriptionKey)}</CardDescription>
                            </CardHeader>
                        </Card>
                    </Link>))}
            </div>

            <p className="text-xs text-muted-foreground">
                {t('dashboard.summary', {
            count: assignments.length,
            assignments: assignments.length,
            payouts: queue.length,
        })}
            </p>
        </div>);
}
function Kpi({ label, value, accent = 'default', }) {
    return (<Card>
            <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className={'text-3xl font-semibold ' +
            (accent === 'danger'
                ? 'text-red-500'
                : accent === 'warning'
                    ? 'text-amber-500'
                    : 'text-foreground')}>
                    {value}
                </p>
            </CardContent>
        </Card>);
}
function TrendsCard() {
    const { data = [], isLoading } = useQuery({
        queryKey: ['internal-dashboard', 'trends', 30],
        queryFn: () => fetchTrends(30),
        staleTime: 60_000,
    });
    return (<Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <TrendingUp className="h-4 w-4"/>
                    Last 30 days — payouts &amp; scans
                </CardTitle>
            </CardHeader>
            <CardContent className="h-64">
                {isLoading ? (<p className="text-sm text-muted-foreground">Loading…</p>) : (<ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)"/>
                            <XAxis dataKey="date" stroke="#9a9a9a" fontSize={11} tickFormatter={(v) => v.slice(5)}/>
                            <YAxis stroke="#9a9a9a" fontSize={11} allowDecimals={false}/>
                            <Tooltip contentStyle={{ background: '#111', border: '1px solid #2a2a2a' }}/>
                            <Legend />
                            <Line type="monotone" dataKey="payouts" stroke="#3F51B5" dot={false}/>
                            <Line type="monotone" dataKey="scans" stroke="#10b981" dot={false}/>
                        </LineChart>
                    </ResponsiveContainer>)}
            </CardContent>
        </Card>);
}
function TelegramHealthCard() {
    const { t } = useTranslation('internal');
    // Adora runs two distinct Telegram bot identities (see BotProfileEnum) —
    // the dashboard surfaces both so staff can spot a misconfigured webhook
    // on either side without drilling into the settings page.
    return (<Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <Webhook className="h-4 w-4"/>
                    {td(t, 'dashboard.telegram.title')}
                </CardTitle>
                <CardDescription>{td(t, 'dashboard.telegram.desc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
                <TelegramHealthRow profile="internal" label={td(t, 'dashboard.telegram.internal')}/>
                <div className="border-t"/>
                <TelegramHealthRow profile="public" label={td(t, 'dashboard.telegram.public')}/>
                <div className="flex gap-2 pt-1">
                    <Button asChild size="sm" variant="outline">
                        <Link to="/console/internal/settings">
                            <Send className="h-3.5 w-3.5"/>
                            {td(t, 'dashboard.telegram.settings')}
                        </Link>
                    </Button>
                </div>
            </CardContent>
        </Card>);
}
function TelegramHealthRow({ profile, label }) {
    const { t } = useTranslation('internal');
    const { data, isFetching, refetch } = useQuery({
        queryKey: ['telegram-webhook-info', profile],
        queryFn: () => fetchTelegramWebhookInfo(profile),
        retry: false,
    });
    const registered = data?.ok && Boolean(data.info?.url);
    const tokenMissing = data?.ok === false && data?.reason === 'bot_token_missing';
    const lastError = data?.info?.last_error_message;
    return (<div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => refetch()} disabled={isFetching}>
                    {td(t, 'dashboard.telegram.refresh')}
                </Button>
            </div>
            {tokenMissing ? (<p className="flex items-center gap-1.5 text-muted-foreground">
                    <XCircle className="h-3.5 w-3.5"/>
                    {td(t, 'dashboard.telegram.token_missing')}
                </p>) : registered ? (<p className="flex items-center gap-1.5 text-emerald-500">
                    <CheckCircle2 className="h-3.5 w-3.5"/>
                    {td(t, 'dashboard.telegram.registered')}
                </p>) : (<p className="flex items-center gap-1.5 text-amber-500">
                    <XCircle className="h-3.5 w-3.5"/>
                    {td(t, 'dashboard.telegram.not_registered')}
                </p>)}
            {lastError && (<p className="text-xs text-red-500">{td(t, 'dashboard.telegram.last_error', { error: lastError })}</p>)}
        </div>);
}
function ActivityCard() {
    const { t } = useTranslation('internal');
    const { data = [], isLoading } = useQuery({
        queryKey: ['internal-dashboard', 'activity'],
        queryFn: () => fetchActivity(15),
        staleTime: 30_000,
    });
    return (<Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <Activity className="h-4 w-4"/>
                    {td(t, 'dashboard.activity.title')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {isLoading ? (<p className="text-sm text-muted-foreground">{t('common.loading')}</p>) : data.length === 0 ? (<p className="text-sm text-muted-foreground">{td(t, 'dashboard.activity.empty')}</p>) : (<ul className="space-y-2 max-h-80 overflow-y-auto">
                        {data.map((event, i) => {
                const stateKey = event.titleParams?.stateKey;
                const params = stateKey
                    ? { state: td(t, `enum.payout_state.${stateKey}`) }
                    : undefined;
                return (<li key={i} className="text-sm border-b last:border-b-0 pb-2 last:pb-0">
                                    <p className="font-medium">{td(t, event.titleKey, params)}</p>
                                    <p className="text-xs text-muted-foreground">{event.subtitle}</p>
                                    <p className="text-[10px] uppercase text-muted-foreground/70">
                                        {new Date(event.occurredAt).toLocaleString()}
                                    </p>
                                </li>);
            })}
                    </ul>)}
            </CardContent>
        </Card>);
}
function LeaderboardsCard() {
    const { t } = useTranslation('internal');
    const { data, isLoading } = useQuery({
        queryKey: ['internal-dashboard', 'leaderboards'],
        queryFn: fetchLeaderboards,
        staleTime: 60_000,
    });
    return (<Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <Crown className="h-4 w-4"/>
                    {td(t, 'dashboard.leaderboards.title')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {isLoading ? (<p className="text-sm text-muted-foreground">{t('common.loading')}</p>) : (<>
                        <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                                {td(t, 'dashboard.leaderboards.drivers')}
                            </p>
                            <ul className="space-y-1 text-sm">
                                {(data?.drivers ?? []).map((d) => (<li key={d.driverId} className="flex justify-between">
                                        <span>{d.name}</span>
                                        <span className="text-muted-foreground">
                                            {td(t, 'dashboard.leaderboards.payouts_count', { count: d.payouts })}
                                        </span>
                                    </li>))}
                                {(data?.drivers ?? []).length === 0 && (<li className="text-muted-foreground">{td(t, 'dashboard.leaderboards.no_data')}</li>)}
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                                {td(t, 'dashboard.leaderboards.campaigns')}
                            </p>
                            <ul className="space-y-1 text-sm">
                                {(data?.campaigns ?? []).map((c) => (<li key={c.campaignId} className="flex justify-between">
                                        <span>{c.name}</span>
                                        <span className="text-muted-foreground">
                                            {td(t, 'dashboard.leaderboards.scans_count', { count: c.scans })}
                                        </span>
                                    </li>))}
                                {(data?.campaigns ?? []).length === 0 && (<li className="text-muted-foreground">{td(t, 'dashboard.leaderboards.no_data')}</li>)}
                            </ul>
                        </div>
                    </>)}
            </CardContent>
        </Card>);
}
function WorkspaceOverviewCard() {
    const { t } = useTranslation('internal');
    const { data, isLoading } = useQuery({
        queryKey: ['internal-dashboard', 'workspaces'],
        queryFn: fetchWorkspaceOverview,
        staleTime: 60_000,
    });
    return (<Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <Building2 className="h-4 w-4"/>
                    {td(t, 'dashboard.platform.title')}
                </CardTitle>
                <CardDescription>{td(t, 'dashboard.platform.desc')}</CardDescription>
            </CardHeader>
            <CardContent>
                {isLoading || !data ? (<p className="text-sm text-muted-foreground">{t('common.loading')}</p>) : (<div className="grid gap-4 md:grid-cols-5">
                        <Kpi label={td(t, 'dashboard.platform.workspaces')} value={data.totalWorkspaces}/>
                        <Kpi label={td(t, 'dashboard.platform.users')} value={data.totalUsers}/>
                        <Kpi label={td(t, 'dashboard.platform.signups_7d')} value={data.signups7d}/>
                        <Kpi label={td(t, 'dashboard.platform.signups_30d')} value={data.signups30d}/>
                        <Kpi label={td(t, 'dashboard.platform.active_subs')} value={data.activeSubs}/>
                        <div className="md:col-span-5">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                                {td(t, 'dashboard.platform.by_plan')}
                            </p>
                            <ul className="space-y-1 text-sm">
                                {data.byPlan.map((row) => (<li key={row.planId} className="flex justify-between">
                                        <span>{row.name}</span>
                                        <span className="text-muted-foreground">{row.total}</span>
                                    </li>))}
                                {data.byPlan.length === 0 && (<li className="text-muted-foreground">{td(t, 'dashboard.platform.no_subs')}</li>)}
                            </ul>
                        </div>
                    </div>)}
            </CardContent>
        </Card>);
}
